/**
 * La cloche des alertes — chunk 9.AF.1.
 *
 * `app.notifications` existait depuis la migration 0006 sans avoir jamais reçu une
 * ligne. Le métier planifié la remplit ; ceci la donne enfin à lire.
 *
 * ## Ce que la cloche ne fait pas
 *
 * Elle n'interrompt pas. Pas de son, pas de bandeau, pas de compteur qui clignote :
 * une échéance à sept jours n'est pas un incident, et une interface qui traite tout
 * comme urgent finit par être ignorée pour tout. La pastille se voit quand on
 * regarde, et c'est suffisant.
 *
 * ## Marquer comme lu ≠ traiter
 *
 * Lire une alerte ne résout pas l'échéance : elle reste dans la liste du dossier et
 * dans les compteurs. Ne faire disparaître que le rappel évite le piège du
 * « inbox zero » où vider la cloche donne le sentiment d'avoir travaillé.
 */
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from "../lib/queries/deadlines";

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("en") ? "en" : "fr";
  const [ouvert, setOuvert] = useState(false);
  const panneauId = useId();
  const conteneur = useRef<HTMLDivElement>(null);
  const bouton = useRef<HTMLButtonElement>(null);

  const req = useNotifications();
  const lire = useMarkNotificationRead();
  const toutLire = useMarkAllNotificationsRead();

  const items = Array.isArray(req.data?.items) ? req.data.items.filter((n) => n?.id) : [];
  const nonLues = req.data?.unread_count ?? 0;

  // Échap ferme et rend le focus au bouton — sans quoi le focus reste dans un
  // panneau qui n'est plus à l'écran, et la tabulation suivante part dans le vide.
  useEffect(() => {
    if (!ouvert) return;
    function surTouche(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOuvert(false);
        bouton.current?.focus();
      }
    }
    function surClic(e: MouseEvent) {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false);
    }
    document.addEventListener("keydown", surTouche);
    document.addEventListener("mousedown", surClic);
    return () => {
      document.removeEventListener("keydown", surTouche);
      document.removeEventListener("mousedown", surClic);
    };
  }, [ouvert]);

  return (
    <div ref={conteneur} className="relative">
      <button
        ref={bouton}
        type="button"
        aria-label={
          nonLues > 0
            ? t("notifications.open_with_unread", { count: nonLues })
            : t("notifications.open")
        }
        aria-expanded={ouvert}
        aria-controls={panneauId}
        onClick={() => setOuvert((v) => !v)}
        className="focus-visible:ring-brand-500 relative inline-flex h-10 w-10 touch-manipulation items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M10 3a4.5 4.5 0 0 0-4.5 4.5c0 2.6-.7 4.2-1.3 5.1a.6.6 0 0 0 .5.9h10.6a.6.6 0 0 0 .5-.9c-.6-.9-1.3-2.5-1.3-5.1A4.5 4.5 0 0 0 10 3Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path d="M8.2 16a2 2 0 0 0 3.6 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {nonLues > 0 && (
          // Le compte est déjà dans `aria-label` du bouton : le répéter ici le
          // ferait annoncer deux fois.
          <span
            aria-hidden="true"
            className="absolute right-1 top-1 inline-flex min-w-[1.15rem] justify-center rounded-full bg-red-600 px-1 text-[0.65rem] font-semibold leading-4 text-white"
          >
            {nonLues > 9 ? "9+" : nonLues}
          </span>
        )}
      </button>

      {ouvert && (
        <div
          id={panneauId}
          className="absolute right-0 z-30 mt-1 w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">{t("notifications.title")}</h2>
            {nonLues > 0 && (
              <button
                type="button"
                disabled={toutLire.isPending}
                onClick={() => toutLire.mutate()}
                className="focus-visible:ring-brand-500 rounded text-xs font-medium text-brand-700 hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("notifications.mark_all_read")}
              </button>
            )}
          </div>

          {req.isPending ? (
            <p className="px-4 py-6 text-center text-sm text-slate-500" aria-live="polite">
              {t("notifications.loading")}
            </p>
          ) : req.isError ? (
            <p className="px-4 py-6 text-center text-sm text-red-800" role="alert">
              {t("notifications.error")}
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-slate-600">
              {t("notifications.empty")}
            </p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {items.map((n) => (
                <li key={n.id} className="border-b border-slate-100 last:border-b-0">
                  <div className="flex items-start gap-2 px-4 py-3">
                    <span className="min-w-0 flex-1">
                      {n.project_id !== null ? (
                        <Link
                          to={`/projects/${n.project_id}`}
                          onClick={() => {
                            setOuvert(false);
                            if (n.read_at === null) lire.mutate(n.id);
                          }}
                          className="focus-visible:ring-brand-500 block rounded text-sm font-medium text-slate-900 hover:underline focus-visible:outline-none focus-visible:ring-2"
                        >
                          {n.title}
                        </Link>
                      ) : (
                        <span className="block text-sm font-medium text-slate-900">{n.title}</span>
                      )}
                      {n.body !== null && (
                        <span className="mt-0.5 block text-xs text-slate-600">{n.body}</span>
                      )}
                      <span className="mt-0.5 block text-xs text-slate-400">
                        {new Date(n.created_at).toLocaleDateString(lang)}
                      </span>
                    </span>
                    {n.read_at === null && (
                      <button
                        type="button"
                        onClick={() => lire.mutate(n.id)}
                        className="focus-visible:ring-brand-500 mt-0.5 shrink-0 rounded border border-slate-300 px-2 py-0.5 text-xs font-medium text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                      >
                        {t("notifications.mark_read")}
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
